import { ReactElement, useContext, useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled, { ThemeContext } from 'styled-components';

import * as graphql from 'generated/graphql';
import ColorPicker from 'packages/kakapo-ui-kit/components/forms/controls/ColorPicker';
import useBreadcrumb from 'packages/kakapo-ui-kit/components/breadcrumb/useBreadcrumb';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import { ImageAspectRatio, ImageShape } from 'packages/kakapo-ui-kit/types/Image';
import ImageUploadModalContainer from 'packages/kakapo-ui-settings-application/pages/theme/ImageUploadModalContainer';
import {
	PageTitle,
	SectionTitle,
	SubSectionTitle,
} from 'packages/kakapo-ui-kit/components/common/Headings';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import treams from 'packages/kakapo-ui-kit/themes/treams';
import OutlineButton from 'packages/kakapo-ui-kit/components/buttons/OutlineButton';
import OrganizationThemeService from 'packages/kakapo-ui-services/services/OrganizationThemeService';
import useUploadAsset from 'packages/kakapo-ui-application/hooks/useUploadAsset';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import useCompleteWorkspaceCheckpoint from 'packages/kakapo-ui-application/hooks/useCompleteWorkspaceCheckpoint';

const LineGradientExample = styled(Box)<{ left: string; right: string }>`
	height: 30px;
	width: 100%;
	background: linear-gradient(90deg, ${(props) => props.left} 0%, ${(props) => props.right} 100%);
`;

const LogoImage = styled.img`
	max-width: 150px;
	max-height: 150px;
	margin-bottom: 16px;
`;

const LoginPhotoImage = styled.img`
	max-width: 300px;
	max-height: 150px;
	margin-bottom: 16px;
`;

const ImagePlaceholder = styled.div<{ width: number }>`
	width: ${(props) => props.width}px;
	height: 150px;
	border: 2px dotted ${(props) => props.theme.colors.lightBlueDark};
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	text-align: center;
	color: ${(props) => props.theme.colors.slateBlue};
	justify-content: center;
`;

export default function SettingsThemeContainer(): ReactElement {
	const { t } = useTranslation();
	const [imageUploaderToShow, setImageUploaderToShow] = useState<null | 'logo' | 'background'>(
		null,
	);
	useBreadcrumb(t('settings.theme.title'));
	const workspace = useWorkspace();
	const { data } = graphql.useWorkspaceThemeQuery({
		variables: { id: workspace.id },
	});
	const theme = useContext(ThemeContext);
	const { colors } = theme;
	const [primaryColor, setPrimaryColor] = useState(colors.primary);
	const [primaryColorDark, setPrimaryColorDark] = useState(colors.primaryDark);
	const [secondaryColor, setSecondaryColor] = useState(colors.secondary);
	const lineGradient: string = theme.images.lineGradient ?? treams.images.lineGradient;
	const lineGradientColors = OrganizationThemeService.parseGradientString(lineGradient);
	const [gradientColors, setGradientColors] = useState(lineGradientColors);
	const [updateColors, { loading: isSavingColors }] = graphql.useUpdateWorkspaceColorsMutation();

	useEffect(() => {
		setPrimaryColor(colors.primary);
		setPrimaryColorDark(colors.primaryDark);
		setSecondaryColor(colors.secondary);
		const parsedGradientColors = OrganizationThemeService.parseGradientString(lineGradient);
		setGradientColors(parsedGradientColors);
	}, [theme]);

	const [updateWorkspaceLogo] = graphql.useUpdateWorkspaceLogoMutation();
	const [resetWorkspaceLogo, { loading: isResettingLogo }] =
		graphql.useResetWorkspaceLogoMutation();

	const completeCheckpoint = useCompleteWorkspaceCheckpoint('workspace.settings.theme');

	const [uploadLogo, { loading: isSavingLogo }] = useUploadAsset(async (assetID) => {
		setImageUploaderToShow(null);
		await updateWorkspaceLogo({
			variables: {
				input: {
					assetID,
				},
			},
		});

		await completeCheckpoint();
	});

	const [updateWorkspaceBackground] = graphql.useUpdateWorkspaceBackgroundMutation();
	const [resetWorkspaceBackground, { loading: isResettingBackground }] =
		graphql.useResetWorkspaceBackgroundMutation();

	const [uploadBackground, { loading: isSavingBackground }] = useUploadAsset(async (assetID) => {
		setImageUploaderToShow(null);
		await updateWorkspaceBackground({
			variables: {
				input: {
					assetID,
				},
			},
		});

		await completeCheckpoint();
	});

	const saveColors = () => {
		updateColors({
			variables: {
				input: {
					primaryColor,
					primaryColorDark,
					secondaryColor,
					linearGradientStart: gradientColors[0],
					linearGradientEnd: gradientColors[1],
				},
			},
		});
	};

	const resetColors = () => {
		updateColors({
			variables: {
				input: {
					primaryColor: null,
					primaryColorDark: null,
					secondaryColor: null,
					linearGradientStart: null,
					linearGradientEnd: null,
				},
			},
		});
	};

	return (
		<Flex flexDirection="column" alignItems="stretch">
			<PageTitle>{t('settings.theme.title')}</PageTitle>
			<Flex mt={3} flexDirection="row" justifyContent="space-between">
				<Box>
					<SectionTitle>{t('settings.theme.logo.title')}</SectionTitle>

					<Flex flexDirection="column" alignItems="start">
						{data?.workspace.theme.logo ? (
							<LogoImage src={data.workspace.theme.logo.source.url} alt="" />
						) : (
							<ImagePlaceholder width={150}>
								<span>{t('common.noImageSet')}</span>
							</ImagePlaceholder>
						)}
						<Flex flexDirection="row">
							<PrimaryButton
								disabled={isSavingLogo || isResettingLogo}
								size={ButtonSize.Small}
								onClick={() => setImageUploaderToShow('logo')}
							>
								{t('settings.theme.updateLogo')}
							</PrimaryButton>
							{data?.workspace.theme.logo && (
								<Box ml={2}>
									<OutlineButton
										disabled={isSavingLogo || isResettingLogo}
										size={ButtonSize.Small}
										onClick={() => resetWorkspaceLogo()}
									>
										{t('settings.theme.resetLogo')}
									</OutlineButton>
								</Box>
							)}
						</Flex>
					</Flex>
				</Box>

				<Box>
					<SectionTitle>{t('settings.theme.loginPhoto.title')}</SectionTitle>

					<Flex flexDirection="column" alignItems="start">
						{data?.workspace.theme.background ? (
							<LoginPhotoImage src={data.workspace.theme.background.source.url} alt="" />
						) : (
							<ImagePlaceholder width={250}>
								<span>{t('common.noImageSet')}</span>
							</ImagePlaceholder>
						)}
						<Flex flexDirection="row">
							<PrimaryButton
								disabled={isSavingBackground || isResettingBackground}
								size={ButtonSize.Small}
								onClick={() => setImageUploaderToShow('background')}
							>
								{t('settings.theme.updateLoginPhoto')}
							</PrimaryButton>
							{data?.workspace.theme.background !== null && (
								<Box ml={2}>
									<OutlineButton
										disabled={isSavingBackground || isResettingBackground}
										size={ButtonSize.Small}
										onClick={() => resetWorkspaceBackground()}
									>
										{t('settings.theme.resetLoginPhoto')}
									</OutlineButton>
								</Box>
							)}
						</Flex>
					</Flex>
				</Box>
			</Flex>
			<Box mt={5}>
				<SectionTitle>{t('settings.theme.colors.title')}</SectionTitle>

				<Flex flexDirection="column" alignItems="stretch">
					<Flex flexDirection="row">
						<Box mr={3}>
							<ColorPicker
								label={t('settings.theme.colors.primary.label')}
								color={primaryColor}
								setColor={setPrimaryColor}
							/>
						</Box>
						<Box mr={3}>
							<ColorPicker
								label={t('settings.theme.colors.primaryDark.label')}
								color={primaryColorDark}
								setColor={setPrimaryColorDark}
							/>
						</Box>
						<Box>
							<ColorPicker
								label={t('settings.theme.colors.secondary.label')}
								color={secondaryColor}
								setColor={setSecondaryColor}
							/>
						</Box>
					</Flex>
					<Box mt={4}>
						<SubSectionTitle>{t('settings.theme.colors.gradient.title')}</SubSectionTitle>
						<Flex justifyContent="space-between" flexDirection="row">
							<ColorPicker
								label={t('settings.theme.colors.gradient.left.label')}
								color={gradientColors[0]}
								setColor={(value) => setGradientColors([value, gradientColors[1]])}
							/>
							<ColorPicker
								label={t('settings.theme.colors.gradient.right.label')}
								color={gradientColors[1]}
								setColor={(value) => setGradientColors([gradientColors[0], value])}
							/>
						</Flex>
						<Box mt={3}>
							<LineGradientExample left={gradientColors[0]} right={gradientColors[1]} />
						</Box>
						<Flex flexDirection="row" mt={3}>
							<PrimaryButton
								disabled={isSavingColors}
								size={ButtonSize.Small}
								onClick={() => saveColors()}
							>
								{t('settings.theme.updateColors')}
							</PrimaryButton>
							<Box ml={2}>
								<OutlineButton
									disabled={isSavingColors}
									size={ButtonSize.Small}
									onClick={() => resetColors()}
								>
									{t('settings.theme.resetColors')}
								</OutlineButton>
							</Box>
						</Flex>
					</Box>
				</Flex>
			</Box>

			{imageUploaderToShow === 'logo' && (
				<ImageUploadModalContainer
					onSubmit={(blob) => uploadLogo(blob!)}
					imageSpecification={{
						title: t('common.logo'),
						key: 'logo',
						aspectRatio: ImageAspectRatio.Logo,
						shape: ImageShape.Rectangular,
						allowedExtensions: ['image/png', 'image/jpeg', 'image/svg+xml', 'application/xml'],
					}}
					onCancel={() => setImageUploaderToShow(null)}
				/>
			)}
			{imageUploaderToShow === 'background' && (
				<ImageUploadModalContainer
					onSubmit={(blob) => uploadBackground(blob!)}
					imageSpecification={{
						title: t('common.loginPhoto'),
						key: 'loginPhoto',
						aspectRatio: ImageAspectRatio.Ratio16by9,
						shape: ImageShape.Rectangular,
						minimumWidth: 1440,
						allowedExtensions: ['image/png', 'image/jpeg'],
					}}
					onCancel={() => setImageUploaderToShow(null)}
				/>
			)}
		</Flex>
	);
}
